/**
 * The strings the **Copy fix prompt** buttons put on the clipboard (`docs/dev/admin/03`).
 *
 * The loop this closes: Namık sees something on `/issues`, presses one button, pastes into
 * Claude Code. So the text has to carry two things at once — the *instruction* (which is
 * fixed, and is the loop from `docs/dev/README.md` §The issues loop written out) and the
 * *substance* (the issue's own facts and note, verbatim, so the agent knows what it is
 * looking at before it runs anything).
 *
 * **Two lanes, because two kinds of finding.** A wrapped label or a singular/plural slip is
 * something a person confirms by looking at it, and wrapping that in a regression test and a
 * full four-command suite costs more than the fix itself. An engine, rules or ledger change
 * is the opposite: nobody can verify it by looking, so it earns the test and the whole
 * suite. *Quick* is the default because most of what a hands-on walk turns up is the first
 * kind; *thorough* is one press away, and the rule for choosing is in the README.
 *
 * The `show` command is in both even though the note is too. That is deliberate: the note is
 * what Namık typed, and the screenshots — which are the half a paste cannot carry — only
 * arrive through `--download`.
 *
 * **The guard flags follow the build.** Running against live, the command carries
 * `--project atlas-nook --confirm`, because the CLI refuses without them on a project shared
 * with the consumer platform. Against the emulator they are omitted, because there they are
 * noise and the guard does not ask for them.
 *
 * Four strings, one module, no templating UI. The invocation is `pnpm --filter … run issues
 * <cmd>`, with **no `--`**: `pnpm … issues` without `run` resolves to pnpm's own built-in
 * `issues` (an alias of `bugs`) and the script never executes at all.
 */

import { PROJECT_ID } from '@atlas-nook/brand';

import type { Issue } from '../data/issues.js';

/** All these strings need of the environment. `adminEnv()` satisfies it. */
export interface PromptEnv {
  useEmulator: boolean;
}

/**
 * Which lane a fix is being asked for.
 *
 * There is **no default** on purpose. Every caller is a button whose own label says which
 * one it is, and a silent default is how the wrong lane ships.
 */
export type FixLane = 'quick' | 'thorough';

/** The CLI's live guard (`guard.ts`), or nothing at all on the emulator. */
function guard(env: PromptEnv): string {
  return env.useEmulator ? '' : ` --project ${PROJECT_ID} --confirm`;
}

/**
 * A step worth printing.
 *
 * `undefined` is the ordinary "outside a flow" case. The other one is a step somebody
 * *typed* as a dash — the table prints `—` for an empty one, so copying that back into the
 * field is a thing a person does — and printing it here would give the line two dashes in a
 * row with nothing between them.
 */
function realStep(step: string | undefined): string | null {
  if (step === undefined) return null;
  const trimmed = step.replace(/^[\s—–-]+$/u, '').trim();
  return trimmed === '' ? null : step.trim();
}

/**
 * `i-0007 · studio · bug · F4.4 — The variant picker forgets what I chose`
 *
 * The **record's** words, not the page's labels: this is read by an agent that will type
 * `--status` values and grep the repo, so `bug` serves it better than `Bug`.
 */
export function issueFactLine(issue: Issue): string {
  const step = realStep(issue.step);
  const facts = [issue.id, issue.area, issue.kind, ...(step === null ? [] : [step])];
  return `${facts.join(' · ')} — ${issue.title}`;
}

/** The open ones, in the order they were handed over. The batch's only selection rule. */
export function openIssues(issues: Issue[]): Issue[] {
  return issues.filter((issue) => issue.status === 'open');
}

/** One issue, ready to paste. */
export function fixPromptFor(issue: Issue, env: PromptEnv, lane: FixLane): string {
  const show = `pnpm --filter @atlas-nook/studio run issues show ${issue.id}${guard(env)} --download /tmp/${issue.id}`;
  const read = `Run \`${show}\` first and read the note and any screenshots.`;

  const instruction =
    lane === 'quick'
      ? `${read} Fix it. Do not write new tests. Run only the touched package's typecheck and existing tests; no full suite, no build, no screenshots. Commit with the issue id; set fixed with the hash. Namık verifies by hand.`
      : `${read} Reproduce it, then set it to fixing, fix it, add a regression test that fails without the fix, run the full checks (typecheck · lint · test · build), commit with the issue id in the message, and set it to fixed with the commit hash in --note. Do not mark it verified.`;

  const lines = [
    `Fix issue ${issue.id} from the admin tracker.`,
    '',
    instruction,
    '',
    issueFactLine(issue),
  ];

  // Verbatim, line breaks and all — it is what he typed, and re-wrapping it would lose the
  // shape of a two-line "what I did / what I saw" note.
  if (issue.note !== undefined) lines.push(issue.note);

  return lines.join('\n');
}

/**
 * Every open issue in one message.
 *
 * It filters to `open` itself rather than trusting the caller, so the text can never
 * disagree with its own first line. The caller decides *which* issues are on the table —
 * the list hands over what the filters are currently showing — and this decides which of
 * those are open.
 */
export function fixPromptForAll(issues: Issue[], env: PromptEnv, lane: FixLane): string {
  const list = `pnpm --filter @atlas-nook/studio run issues list --status open${guard(env)}`;
  const read = `Run \`${list}\`, then for each: show it (with --download),`;

  const instruction =
    lane === 'quick'
      ? `${read} fix it, commit with the issue id, and set it to fixed with the commit hash. One commit per issue. Do not write new tests. Run only the touched package's typecheck and existing tests; no full suite, no build, no screenshots. Namık verifies by hand. Skip an issue you can't fix and say why in its --note.`
      : `${read} reproduce it, set it to fixing, fix it, add a regression test that fails without the fix, run the full checks, commit with the issue id, set it to fixed with the commit hash. One commit per issue. Do not mark anything verified. Skip an issue you can't fix and say why in its --note.`;

  return [
    'Fix the open issues from the admin tracker.',
    '',
    instruction,
    '',
    ...openIssues(issues).map(issueFactLine),
  ].join('\n');
}
